import { debounce } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { useBrandConfiguration } from '../../brands/context';
import { postViewedClassifieds, } from '../../services/analytics';
import { analyticsSortRecord } from '../../types/analyticsSortRecord';
import { useApis } from '../useApis';
const debouncedPostAnalyticsDataFn = debounce((analyticsDataRequest, callbackFn) => {
    callbackFn();
    void postViewedClassifieds(analyticsDataRequest);
}, 1000);
/**
 * Tracks the viewed classifieds on the page using the
 * {@link https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API | Intersection Observer API}.
 * A classified is considered "viewed" when at least 1% of its height appears
 * in the viewport (`threshold: 0.01`). The information about the viewed classifieds
 * is {@link postViewedClassifieds | sent to the server} in batches using a {@link https://lodash.com/docs/#debounce | debounced}
 * function.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API | Intersection Observer API}
 * @see {@link https://lodash.com/docs/#debounce | Lodash debounce}
 * @see {@link postViewedClassifieds | postViewedClassifieds service function}
 */
const useViewedClassifiedsTracking = ({ subtype, place, classifiedIds, page, order, totalCount, }) => {
    const { bffUri } = useApis();
    const { pageSize, analytics } = useBrandConfiguration();
    const observedClassifiedsRef = useRef({});
    const newlyObservedClassifiedsBatchRef = useRef({});
    const handleAnalyticsDataApiCall = useCallback(() => {
        newlyObservedClassifiedsBatchRef.current = {};
    }, []);
    useEffect(() => {
        const elements = document.querySelectorAll('[id^="classified-card-"]');
        observedClassifiedsRef.current = {};
        newlyObservedClassifiedsBatchRef.current = {};
        const classifiedIdsMap = classifiedIds.reduce((acc, id, index) => {
            acc[id] = index;
            return acc;
        }, {});
        const io = new IntersectionObserver((classifiedNodes) => {
            classifiedNodes.forEach((classifiedNode) => {
                const id = classifiedNode.target.id.split('-')[2];
                if (!classifiedNode.isIntersecting || !id || observedClassifiedsRef.current[id])
                    return;
                observedClassifiedsRef.current[id] = true;
                newlyObservedClassifiedsBatchRef.current[id] = true;
            });
            const newlyObservedClassifiedsList = Object.keys(newlyObservedClassifiedsBatchRef.current);
            if (newlyObservedClassifiedsList.length === 0)
                return;
            const analyticsDataRequest = {
                bffUri,
                analyticsData: {
                    subtype,
                    place,
                    portal: analytics.portal,
                    classifieds: newlyObservedClassifiedsList
                        .map((id) => {
                        const classifiedIndex = classifiedIdsMap[id];
                        if (classifiedIndex === undefined)
                            return null;
                        return {
                            classifiedId: id,
                            rank: (page - 1) * pageSize + classifiedIndex + 1,
                            pagenumber: page,
                            results: totalCount,
                            sorttype: analyticsSortRecord[order],
                        };
                    })
                        .filter((classified) => classified !== null),
                },
            };
            debouncedPostAnalyticsDataFn(analyticsDataRequest, handleAnalyticsDataApiCall);
        }, { threshold: 0.01 });
        elements.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [classifiedIds]);
};
export default useViewedClassifiedsTracking;
